import { useEffect, useMemo, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import { fetchComments, addComment } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import Spinner from './Spinner';

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');
const initials = (name) => (name ? name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() : '?');

// Highlight @mentions in a message body.
function renderBody(body) {
  const out = [];
  const re = /@([\w.\-]+)/g;
  let last = 0; let m; let i = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(body.slice(last, m.index));
    out.push(<span key={i++} className="rounded bg-primary/10 px-1 font-medium text-primary">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}

// The message thread of one ticket: list + composer with @mention autocomplete.
// Used by the ticket detail page and embedded in the edit modal, so every
// button is type="button" — inside the modal this sits within the ticket form
// and must never trigger its submit.
export default function MessageThread({ ticketId }) {
  const { members } = useTickets();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const taRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setComments([]);
    fetchComments(ticketId)
      .then((c) => { if (alive) setComments(c); })
      .catch((err) => console.error('Failed to load messages', err))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ticketId]);

  const onBodyChange = (e) => {
    const val = e.target.value;
    setBody(val);
    const upto = val.slice(0, e.target.selectionStart);
    const m = upto.match(/@([\w.\-]*)$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const mentionMatches = useMemo(() => {
    if (mentionQuery === null) return [];
    return members
      .filter((mb) => !mb.is_external)
      .filter((mb) => mb.username.toLowerCase().includes(mentionQuery) || mb.name.toLowerCase().includes(mentionQuery))
      .slice(0, 6);
  }, [mentionQuery, members]);

  const pickMention = (mb) => {
    const ta = taRef.current;
    const pos = ta ? ta.selectionStart : body.length;
    const before = body.slice(0, pos).replace(/@([\w.\-]*)$/, `@${mb.username} `);
    const next = before + body.slice(pos);
    setBody(next);
    setMentionQuery(null);
    setTimeout(() => { ta?.focus(); ta?.setSelectionRange(before.length, before.length); }, 0);
  };

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const c = await addComment(ticketId, body.trim());
      setComments((prev) => [...prev, c]);
      setBody('');
      setMentionQuery(null);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h2 className="mb-2 font-mono text-sm text-muted-foreground">// messages ({comments.length})</h2>
      {loading ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">{initials(c.author?.name)}</span>
              <div className="min-w-0 flex-1 rounded-lg border border-border bg-card px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.author?.name || 'someone'}</span>
                  <span>{fmt(c.created_at)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{renderBody(c.body)}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 ? <p className="text-sm text-muted-foreground">No messages yet — start the thread.</p> : null}
        </div>
      )}

      <div className="relative mt-3">
        {mentionQuery !== null && mentionMatches.length ? (
          <div className="absolute bottom-full left-0 z-10 mb-1 w-64 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
            {mentionMatches.map((mb) => (
              <button
                key={mb.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pickMention(mb); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[9px] font-semibold text-primary">{initials(mb.name)}</span>
                <span className="font-medium">@{mb.username}</span>
                <span className="truncate text-xs text-muted-foreground">{mb.name}</span>
              </button>
            ))}
          </div>
        ) : null}
        <Textarea
          ref={taRef}
          value={body}
          onChange={onBodyChange}
          placeholder="Write a message… type @ to tag someone"
          className="min-h-[70px]"
        />
        <div className="mt-2 flex justify-end">
          <Button type="button" size="sm" onClick={send} disabled={sending || !body.trim()} className="gap-1.5">
            <Send className="h-4 w-4" /> {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
